import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { authApi, projectsApi } from '../../api/services';
import type { Project } from '../../types/models';
import { transcriptsCopy } from '../../copy/transcripts';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// AssignDialog — pick a target executive (same tenant), a project, or both,
// then call back with the selected IDs. The dialog does not hit the assign
// endpoint itself; the parent decides when to submit so it can wire its own
// success / error handling.
// ---------------------------------------------------------------------------

const NONE = '__none__';

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
}

interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetExecutiveUserId?: string, projectId?: string) => Promise<void> | void;
  extractionTitle?: string;
}

export function AssignDialog({
  open,
  onClose,
  onConfirm,
  extractionTitle,
}: AssignDialogProps) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    Promise.all([
      authApi.getUsers().catch(() => [] as UserRow[]),
      projectsApi.getAll({ includeArchived: false }).catch(() => [] as Project[]),
    ])
      .then(([u, p]) => {
        if (cancelled) return;
        setUsers((u as UserRow[]).filter((x) => x.isActive));
        setProjects(p as Project[]);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Unable to load executives and projects.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedUserId('');
      setSelectedProjectId('');
      setSubmitError(null);
    }
  }, [open]);

  const canSubmit = useMemo(
    () => Boolean(selectedUserId || selectedProjectId),
    [selectedUserId, selectedProjectId],
  );

  const handleConfirm = async () => {
    if (!canSubmit) {
      setSubmitError(transcriptsCopy.assignValidationChoose);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onConfirm(selectedUserId || undefined, selectedProjectId || undefined);
      onClose();
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error ?? e?.message ?? 'Unable to assign.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{transcriptsCopy.assignDialogTitle}</DialogTitle>
        </DialogHeader>
        {extractionTitle && (
          <p className="text-sm text-muted-foreground">{extractionTitle}</p>
        )}
        <p className="text-sm">{transcriptsCopy.assignDialogBody}</p>

        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="a-user">{transcriptsCopy.assignToExecutiveLabel}</Label>
              <Select
                value={selectedUserId || NONE}
                onValueChange={(v) => setSelectedUserId(v === NONE ? '' : v)}
              >
                <SelectTrigger id="a-user">
                  <SelectValue placeholder={transcriptsCopy.assignSelectExecutive} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{transcriptsCopy.assignSelectExecutive}</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.displayName} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="a-project">{transcriptsCopy.assignToProjectLabel}</Label>
              <Select
                value={selectedProjectId || NONE}
                onValueChange={(v) => setSelectedProjectId(v === NONE ? '' : v)}
              >
                <SelectTrigger id="a-project">
                  <SelectValue placeholder={transcriptsCopy.assignSelectProject} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{transcriptsCopy.assignSelectProject}</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {loadError && (
          <Alert variant="error">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}
        {submitError && (
          <Alert variant="warning">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {transcriptsCopy.assignCancel}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit || submitting}
            className="gap-1.5"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Assigning…' : transcriptsCopy.assignConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssignDialog;
